import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Analytics Tracker Component
 * Automatically tracks page views on route changes
 */
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Check if user has consented to analytics cookies
    const cookieConsent = localStorage.getItem('cookie_consent');
    let analyticsEnabled = false;
    
    if (cookieConsent) {
      try {
        const consent = JSON.parse(cookieConsent);
        analyticsEnabled = consent.analytics === true;
      } catch (e) {
        analyticsEnabled = false;
      }
    }

    if (!analyticsEnabled) return;

    // Track page view
    const trackPageView = async () => {
      try {
        await fetch(`${API_URL}/api/analytics/pageview`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            page: location.pathname,
            referrer: document.referrer || null,
            user_agent: navigator.userAgent,
            screen_width: window.screen.width,
            screen_height: window.screen.height,
            language: navigator.language
          }),
        });
      } catch (error) {
        // Silently fail - don't affect user experience
        console.debug('Analytics tracking failed:', error);
      }
    };

    trackPageView();
  }, [location.pathname]);

  return null;
};

/**
 * Track custom events
 */
export const trackEvent = async (eventType, page, contentId = null, metadata = null) => {
  // Check consent
  const cookieConsent = localStorage.getItem('cookie_consent');
  if (!cookieConsent) return;
  
  try {
    const consent = JSON.parse(cookieConsent);
    if (!consent.analytics) return;
  } catch (e) {
    return;
  }

  try {
    const params = new URLSearchParams({
      event_type: eventType,
      page: page
    });
    if (contentId) params.append('content_id', contentId);
    
    await fetch(`${API_URL}/api/analytics/event?${params.toString()}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: metadata ? JSON.stringify(metadata) : null,
    });
  } catch (error) {
    console.debug('Event tracking failed:', error);
  }
};

/**
 * Track content view
 */
export const trackContentView = (contentId, contentTitle) => {
  trackEvent('content_view', `/content/${contentId}`, contentId, { title: contentTitle });
};

/**
 * Track conversion (booking request, inquiry, registration)
 */
export const trackConversion = (conversionType, metadata = {}) => {
  trackEvent('conversion', window.location.pathname, null, { type: conversionType, ...metadata });
};

export default AnalyticsTracker;
